import type { HowToBlockInput } from "@/lib/structured-data"

/**
 * Visible, server-rendered render of a HowTo block. Pairs with `HowToSchema`
 * (which emits the JSON-LD from the same `HowToBlockInput`) so the on-page steps
 * match the structured data, per Google's HowTo content policy.
 *
 * Rendered from a tool's server `page.tsx` shell — OUTSIDE the `"use client"`
 * calculator island — so the steps land in the crawlable first-pass HTML. The
 * tool's interactive island may also show the same steps (e.g. a collapsible
 * "how to draw" card); both read the same data object, so they can't drift.
 */
interface HowToStepsProps {
  block: HowToBlockInput
  /** Optional heading override; defaults to the block name. */
  heading?: string
}

export function HowToSteps({ block, heading }: HowToStepsProps) {
  return (
    <section
      aria-labelledby="howto-steps-heading"
      className="mx-auto w-full max-w-5xl px-4 pb-4 md:px-6 lg:px-8 md:pb-8"
    >
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <h2
          id="howto-steps-heading"
          className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl"
        >
          {heading ?? block.name}
        </h2>
        {block.description && (
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 sm:text-base">
            {block.description}
          </p>
        )}
        <ol className="mt-5 space-y-4">
          {block.steps.map((step, i) => (
            <li key={step.name} id={`step-${i + 1}`} className="flex gap-4">
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 text-sm font-bold text-green-800"
              >
                {i + 1}
              </span>
              <div>
                <p className="font-semibold text-gray-900">{step.name}</p>
                <p className="mt-0.5 text-sm leading-relaxed text-gray-700 sm:text-base">
                  {step.text}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}
